import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAppSettings } from '../context/SettingsContext';
import {
  dayCount,
  fetchIncomeReport,
  formatBucketLabel,
  type CompareMode,
  type Granularity,
  type IncomeReport,
} from '../services/incomeReporting';
import { DATE_PRESETS, type DatePresetId, presetById } from '../utils/salesPeriodPresets';

const PAID = {
  stripe: '#635bff',
  xero: '#059669',
};
const INVOICED = {
  stripe: '#c4b5fd',
  xero: '#6ee7b7',
};

function fmtMoney(n: number, cur = 'AUD'): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${n.toFixed(0)} ${cur}`;
  }
}

function trendPct(current: number, prior: number): number | null {
  if (prior === 0) {
    return current === 0 ? null : 100;
  }
  return Math.round(((current - prior) / prior) * 1000) / 10;
}

function TrendBadge({ pct, label }: { pct: number | null | undefined; label: string }) {
  if (pct == null) {
    return null;
  }
  const up = pct >= 0;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
      {up ? (
        <TrendingUpIcon sx={{ fontSize: 16, color: '#22c55e' }} />
      ) : (
        <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444' }} />
      )}
      <Typography variant="caption" fontWeight={700} sx={{ color: up ? '#22c55e' : '#ef4444' }}>
        {up ? '+' : ''}
        {pct}% {label}
      </Typography>
    </Stack>
  );
}

const OverviewPage: React.FC = () => {
  const { settings } = useAppSettings();
  const navigate = useNavigate();

  const [preset, setPreset] = useState<DatePresetId | null>('last30');
  const [from, setFrom] = useState(presetById('last30').from);
  const [to, setTo] = useState(presetById('last30').to);
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [compare, setCompare] = useState<CompareMode>('none');
  const [includeInvoiced, setIncludeInvoiced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<IncomeReport | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchIncomeReport(settings, { from, to, granularity, compare, includeInvoiced });
      setReport(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sales data');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [settings, from, to, granularity, compare, includeInvoiced]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyPreset = (id: DatePresetId) => {
    const r = presetById(id);
    setPreset(id);
    setFrom(r.from);
    setTo(r.to);
    if (id === 'last7' || id === 'last30') {
      setGranularity('day');
    } else if (id === 'last90' || id === 'mtd') {
      setGranularity('week');
    } else {
      setGranularity('month');
    }
  };

  const cur = report?.currency || 'AUD';
  const days = report ? dayCount(report.from, report.to) : 30;
  const compareLabel = compare === 'prior_year' ? 'vs last year' : compare === 'prior_period' ? 'vs prior period' : '';

  const totalPaidTrend = useMemo(() => {
    if (!report?.comparison) {
      return null;
    }
    return trendPct(report.totalPaid, report.comparison.totalPaid);
  }, [report]);

  const totalInvoicedTrend = useMemo(() => {
    if (!report?.comparison || !includeInvoiced) {
      return null;
    }
    return trendPct(report.totalInvoiced, report.comparison.totalInvoiced);
  }, [report, includeInvoiced]);

  const timeSeriesChart = useMemo(() => {
    if (!report) {
      return [];
    }
    return report.series.map((row, i) => ({
      label: formatBucketLabel(row.date, report.granularity),
      stripePaid: row.stripePaid ?? row.stripe ?? 0,
      xeroPaid: row.xeroPaid ?? row.xero ?? 0,
      stripeInvoiced: includeInvoiced ? row.stripeInvoiced ?? 0 : 0,
      xeroInvoiced: includeInvoiced ? row.xeroInvoiced ?? 0 : 0,
      totalPaid: row.totalPaid ?? row.total ?? 0,
      comparePaid: report.comparison?.series[i]?.totalPaid ?? report.comparison?.series[i]?.total ?? 0,
    }));
  }, [report, includeInvoiced]);

  const channelMixData = useMemo(() => {
    if (!report) {
      return [];
    }
    return report.channels
      .filter((c) => c.channel === 'stripe' || c.channel === 'xero')
      .flatMap((c) => {
        const prior = report.comparison?.channels.find((p) => p.channel === c.channel);
        const rows = [
          {
            name: `${c.label} paid`,
            key: `${c.channel}-paid`,
            amount: c.amount,
            prior: prior?.amount ?? 0,
            color: c.channel === 'stripe' ? PAID.stripe : PAID.xero,
          },
        ];
        if (includeInvoiced) {
          rows.push({
            name: `${c.label} invoiced`,
            key: `${c.channel}-inv`,
            amount: c.invoicedAmount ?? 0,
            prior: prior?.invoicedAmount ?? 0,
            color: c.channel === 'stripe' ? INVOICED.stripe : INVOICED.xero,
          });
        }
        return rows;
      });
  }, [report, includeInvoiced]);

  return (
    <Box sx={{ maxWidth: 1400 }}>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 45%, #635bff 140%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
          <HomeOutlinedIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" fontWeight={800} lineHeight={1.2}>
              Sales console
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>
              Combined revenue from all channels — paid cash and issued invoices
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={2}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {DATE_PRESETS.map((p) => (
              <Chip
                key={p.id}
                label={p.label}
                clickable
                onClick={() => applyPreset(p.id)}
                sx={{
                  bgcolor: preset === p.id ? 'rgba(249,115,22,0.9)' : 'rgba(255,255,255,0.12)',
                  color: '#fff',
                  fontWeight: preset === p.id ? 700 : 500,
                  '&:hover': { bgcolor: preset === p.id ? 'rgba(249,115,22,1)' : 'rgba(255,255,255,0.2)' },
                }}
              />
            ))}
          </Stack>

          <Stack direction={{ xs: 'column', lg: 'row' }} flexWrap="wrap" gap={2} alignItems="center">
            <TextField
              label="From"
              type="date"
              size="small"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPreset(null);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.95)' } }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPreset(null);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.95)' } }}
            />

            <ToggleButtonGroup
              size="small"
              exclusive
              value={granularity}
              onChange={(_, v: Granularity | null) => v && setGranularity(v)}
              sx={{ bgcolor: 'rgba(255,255,255,0.1)', '& .MuiToggleButton-root': { color: '#fff', borderColor: 'rgba(255,255,255,0.25)' } }}
            >
              <ToggleButton value="day">Day</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={compare}
              onChange={(_, v: CompareMode | null) => v && setCompare(v)}
              sx={{ bgcolor: 'rgba(255,255,255,0.1)', '& .MuiToggleButton-root': { color: '#fff', borderColor: 'rgba(255,255,255,0.25)' } }}
            >
              <ToggleButton value="none">No compare</ToggleButton>
              <ToggleButton value="prior_period">Prior period</ToggleButton>
              <ToggleButton value="prior_year">Last year</ToggleButton>
            </ToggleButtonGroup>

            <FormControlLabel
              control={
                <Switch
                  checked={includeInvoiced}
                  onChange={(e) => setIncludeInvoiced(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#fbbf24' } }}
                />
              }
              label="Include invoiced"
              sx={{ color: '#fff', mr: 0 }}
            />

            <Button
              variant="contained"
              onClick={() => void load()}
              disabled={loading}
              sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' } }}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => void load()}
              disabled={loading}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(145deg, #fef3c7, #fff)', border: '1px solid #fde68a' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                Paid revenue ({days}d)
              </Typography>
              {loading ? (
                <Skeleton height={48} width="70%" sx={{ mt: 1 }} />
              ) : (
                <>
                  <Typography variant="h4" fontWeight={800} color="primary.dark" sx={{ mt: 0.5 }}>
                    {fmtMoney(report?.totalPaid ?? 0, cur)}
                  </Typography>
                  {compare !== 'none' && <TrendBadge pct={totalPaidTrend} label={compareLabel} />}
                </>
              )}
              {report?.comparison && compare !== 'none' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Compare: {fmtMoney(report.comparison.totalPaid, cur)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {includeInvoiced && (
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Invoiced (issued)
                </Typography>
                {loading ? (
                  <Skeleton height={40} sx={{ mt: 1 }} />
                ) : (
                  <>
                    <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                      {fmtMoney(report?.totalInvoiced ?? 0, cur)}
                    </Typography>
                    {compare !== 'none' && <TrendBadge pct={totalInvoicedTrend} label={compareLabel} />}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {(report?.channels || [])
          .filter((c) => c.channel === 'stripe' || c.channel === 'xero')
          .map((ch) => (
            <Grid item xs={12} sm={6} md={includeInvoiced ? 3 : 4} key={ch.channel}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        {ch.label}
                      </Typography>
                      {loading ? (
                        <Skeleton height={36} width={120} sx={{ mt: 1 }} />
                      ) : (
                        <>
                          <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5 }}>
                            {fmtMoney(ch.amount, ch.currency)} paid
                          </Typography>
                          {includeInvoiced && (
                            <Typography variant="body2" color="text.secondary">
                              {fmtMoney(ch.invoicedAmount ?? 0, ch.currency)} invoiced
                            </Typography>
                          )}
                          {compare !== 'none' && ch.trendPct != null && (
                            <TrendBadge pct={ch.trendPct} label={compareLabel} />
                          )}
                        </>
                      )}
                    </Box>
                    {ch.channel === 'stripe' ? (
                      <CreditCardOutlinedIcon sx={{ color: PAID.stripe, fontSize: 32 }} />
                    ) : (
                      <AccountBalanceOutlinedIcon sx={{ color: PAID.xero, fontSize: 32 }} />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={compare !== 'none' ? 8 : 12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                Revenue by channel
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Stacked columns — solid = paid (cash), pastel = invoiced (issued){' '}
                · grouped by {granularity}
              </Typography>
              <Box sx={{ width: '100%', height: 380 }}>
                {loading ? (
                  <Skeleton variant="rounded" height={380} />
                ) : timeSeriesChart.length ? (
                  <ResponsiveContainer>
                    <ComposedChart data={timeSeriesChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip formatter={(v) => fmtMoney(Number(v ?? 0), cur)} />
                      <Legend />
                      <Bar dataKey="stripePaid" name="Stripe paid" stackId="rev" fill={PAID.stripe} />
                      <Bar
                        dataKey="xeroPaid"
                        name="Xero paid"
                        stackId="rev"
                        fill={PAID.xero}
                        radius={includeInvoiced ? [0, 0, 0, 0] : [4, 4, 0, 0]}
                      />
                      {includeInvoiced && (
                        <>
                          <Bar dataKey="stripeInvoiced" name="Stripe invoiced" stackId="rev" fill={INVOICED.stripe} />
                          <Bar
                            dataKey="xeroInvoiced"
                            name="Xero invoiced"
                            stackId="rev"
                            fill={INVOICED.xero}
                            radius={[4, 4, 0, 0]}
                          />
                        </>
                      )}
                      {compare !== 'none' && (
                        <Line
                          type="monotone"
                          dataKey="comparePaid"
                          name={compare === 'prior_year' ? 'Prior year (paid)' : 'Prior period (paid)'}
                          stroke="#64748b"
                          strokeWidth={2}
                          dot={false}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" py={8} textAlign="center">
                    No data in this period — connect Stripe and Xero in API settings.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {compare !== 'none' && report?.comparison && (
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                  Comparison
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {report.from} → {report.to} vs {report.comparison.from} → {report.comparison.to}
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      Paid revenue
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                      {fmtMoney(report.totalPaid, cur)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      was {fmtMoney(report.comparison.totalPaid, cur)}
                    </Typography>
                    <TrendBadge pct={totalPaidTrend} label={compareLabel} />
                  </Box>
                  {includeInvoiced && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Invoiced
                      </Typography>
                      <Typography variant="h5" fontWeight={800}>
                        {fmtMoney(report.totalInvoiced, cur)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        was {fmtMoney(report.comparison.totalInvoiced, cur)}
                      </Typography>
                      <TrendBadge pct={totalInvoicedTrend} label={compareLabel} />
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                Channel breakdown
              </Typography>
              <Box sx={{ width: '100%', height: 320 }}>
                {loading ? (
                  <Skeleton variant="rounded" height={320} />
                ) : (
                  <ResponsiveContainer>
                    <BarChart data={channelMixData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip formatter={(v) => fmtMoney(Number(v ?? 0), cur)} />
                      <Legend />
                      <Bar dataKey="amount" name="This period" radius={[6, 6, 0, 0]}>
                        {channelMixData.map((c) => (
                          <Cell key={c.key} fill={c.color} />
                        ))}
                      </Bar>
                      {compare !== 'none' && (
                        <Bar dataKey="prior" name={compare === 'prior_year' ? 'Last year' : 'Prior period'} fill="#cbd5e1" radius={[6, 6, 0, 0]} />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button size="small" variant="outlined" onClick={() => navigate('/stripe/overview')}>
          Stripe
        </Button>
        <Button size="small" variant="outlined" onClick={() => navigate('/xero/reports')}>
          Xero
        </Button>
        <Button size="small" variant="outlined" onClick={() => navigate('/reports/income')}>
          Income report
        </Button>
      </Stack>
    </Box>
  );
};

export default OverviewPage;
